import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { joApi, joErr } from '@/services/processingJoApi';
import { fmtDate, fmtN, routeLabel } from './JoBits';
import { PoTag } from './JoSources';

const Step = ({ label, children, testId }) => (
  <div className="min-w-[150px] rounded-md border border-[#E5DFD0] bg-white px-3 py-2" data-testid={testId}>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</div>
    <div className="text-sm text-gray-900">{children}</div>
  </div>
);

const Arrow = () => <ArrowRight className="h-4 w-4 shrink-0 text-amber-500" />;

/** One job order the code belongs to: PO → Greige receipt → Greige bundle → job order → jobber → the scanned code. */
const Chain = ({ r, res, onOpen }) => (
  <div className="rounded-lg border border-[#E5DFD0] bg-[#FBF8F1] p-3" data-testid={`trace-chain-${r.jo_id}-${r.greige_bundle}`}>
    <div className="mb-2 flex items-center gap-2 text-xs text-gray-600"><Badge className="border-0 bg-[#1A1F2E] text-white">{fmtN(r.pcs)} pcs</Badge>of {res.code} came this way</div>
    <div className="flex flex-wrap items-center gap-2">
      <Step label="Purchase order" testId={`trace-po-${r.greige_bundle}`}><PoTag po={r.po_number} testId={`trace-po-tag-${r.greige_bundle}`} /></Step><Arrow />
      <Step label="Greige receipt" testId={`trace-doc-${r.greige_bundle}`}><span className="font-mono text-xs">{r.document || '—'}</span><div className="text-xs text-gray-500">{r.supplier || 'Supplier unknown'}</div></Step><Arrow />
      <Step label="Greige bundle" testId={`trace-bundle-${r.greige_bundle}`}><span className="font-mono text-xs">{r.greige_bundle || '—'}</span><div className="text-xs text-gray-500">{[r.sort_no && `Sort ${r.sort_no}`, r.colour].filter(Boolean).join(' · ')}</div></Step><Arrow />
      <Step label="Job order" testId={`trace-jo-${r.jo_id}`}>
        <button type="button" onClick={() => onOpen(r.jo_id)} className="font-mono text-xs font-semibold text-sky-700 hover:underline" data-testid={`trace-jo-link-${r.jo_id}`}>{r.jo_id}</button>
        <div className="text-xs text-gray-500">{r.fg_name}{r.design_name ? ` · ${r.design_name}` : ''} · {routeLabel(r.route)}</div></Step><Arrow />
      <Step label="Jobber" testId={`trace-jobber-${r.greige_bundle}`}>{r.jobber_name || r.send_to || 'Not assigned yet'}
        <div className="text-xs text-gray-500">{r.transfer_id} · sent {fmtDate(r.sent_at)}{r.gate_out_at ? ` · out ${fmtDate(r.gate_out_at)}` : ''}</div></Step>
    </div>
  </div>
);

const Result = ({ res, onOpen }) => (
  <div className="space-y-3" data-testid="trace-result">
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-lg border-l-4 border-amber-500 bg-white px-4 py-3 shadow-sm">
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-testid="trace-kind">{res.kind_label}</span>
      <span className="font-mono text-lg font-bold text-gray-900" data-testid="trace-code-value">{res.code}</span>
      <span className="text-sm text-gray-600" data-testid="trace-pcs">{fmtN(res.pcs)} pcs</span>
      {res.now && <span className="text-sm text-gray-700" data-testid="trace-now">· {res.now.text}{res.now.at ? ` (${fmtDate(res.now.at)})` : ''}</span>}
    </div>
    {!res.rows.length && <p className="text-sm text-gray-500" data-testid="trace-no-jo">Not part of any Processing job order</p>}
    {res.rows.map((r) => <Chain key={`${r.jo_id}-${r.greige_bundle}`} r={r} res={res} onOpen={onOpen} />)}
    {res.unlinked_pcs > 0 && <p className="text-xs text-gray-500" data-testid="trace-unlinked">{fmtN(res.unlinked_pcs)} pcs of this code are not on any job order</p>}
  </div>
);

/** Scan any SFG box, Greige / Processing Inward bundle or piece sticker → its job order, jobber, Greige document, supplier and PO. */
export const TraceCodePanel = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const [code, setCode] = useState('');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try { setRes(await joApi.traceCode(code.trim())); } catch (er) { setRes(null); toast.error(joErr(er, 'Could not trace this code')); }
    finally { setBusy(false); ref.current?.select(); }
  };
  return (
    <div className="space-y-4" data-testid="jo-trace-code">
      <form onSubmit={go} className="flex max-w-xl gap-2">
        <Input ref={ref} autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="Scan an SFG box, bundle or piece sticker" className="bg-white font-mono" data-testid="trace-code-input" />
        <Button type="submit" disabled={busy || !code.trim()} className="bg-[#1A1F2E] text-white hover:bg-[#2A3142]" data-testid="trace-code-btn">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ScanLine className="mr-1 h-4 w-4" />}Trace</Button>
      </form>
      {res ? <Result res={res} onOpen={(id) => navigate(`/sections/processing/job-orders/${id}`)} />
        : <p className="text-sm text-gray-500">Shows which job order, jobber, Greige document, supplier and Purchase Order the goods came from.</p>}
    </div>
  );
};

export default TraceCodePanel;
